import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export type AlertType = 'success' | 'error' | 'info';

export interface Alert {
    id: string;
    message: string;
    type: AlertType;
}

interface AlertContextType {
    alerts: Alert[];
    showAlert: (message: string, type?: AlertType) => void;
    success: (message: string) => void;
    error: (message: string) => void;
    info: (message: string) => void;
    removeAlert: (id: string) => void;
}

const AlertContext = createContext<AlertContextType | undefined>(undefined);

export const AlertProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [alerts, setAlerts] = useState<Alert[]>([]);

    const removeAlert = useCallback((id: string) => {
        setAlerts((prev) => prev.filter((alert) => alert.id !== id));
    }, []);

    const showAlert = useCallback((message: string, type: AlertType = 'info') => {
        const id = Math.random().toString(36).substr(2, 9);
        setAlerts((prev) => [...prev, { id, message, type }]);

        // Auto-dismiss after 5 seconds
        setTimeout(() => {
            removeAlert(id);
        }, 5000);
    }, [removeAlert]);

    const success = useCallback((message: string) => showAlert(message, 'success'), [showAlert]);
    const error = useCallback((message: string) => showAlert(message, 'error'), [showAlert]);
    const info = useCallback((message: string) => showAlert(message, 'info'), [showAlert]);

    return (
        <AlertContext.Provider value={{ alerts, showAlert, success, error, info, removeAlert }}>
            {children}
        </AlertContext.Provider>
    );
};

export const useAlert = () => {
    const context = useContext(AlertContext);
    if (context === undefined) {
        throw new Error('useAlert must be used within an AlertProvider');
    }
    return context;
};
